import { NOTE_NAMES, KEYBOARD_MAP, INSTRUMENTS } from "./constants.js";
import { playNote, stopNote, loadInstrument } from "./audio.js";
import { generateKeyboard, updateKeyboard } from "./keyboard.js";
import { saveSettings, loadSettings } from "./settings.js";
import { WebAudioFontPlayer } from "./webAudioFontWrapper.js";

let audioContext = null;
let player = null;
let currentNote = null;
let selectedInstrument = null;
let hasGuessed = false;
let activeNotes = new Set();
let numCorrect = 0;
let numWrong = 0;
let totalSemitoneDistance = 0;

// Keyboard input variables
let currentOctaveOffset = 0;
const activeKeyboardNotes = new Set();

// Add at the top with other state variables
let playKeyPressed = false;

// Add keyboard event listeners
document.addEventListener("keydown", handleKeyDown);
document.addEventListener("keyup", handleKeyUp);

// Add near the top with other event listeners, before the playNoteHandler listeners
document.addEventListener('keydown', (event) => {
  // Prevent spacebar from scrolling the page, but only when not in an input/select
  if (event.code === 'Space' && 
      document.activeElement.tagName !== 'INPUT' && 
      document.activeElement.tagName !== 'SELECT') {
    event.preventDefault();
  }
});

function handleKeyDown(event) {
  if (!audioContext) return;

  const key = event.key.toLowerCase();

  // Handle octave shifts
  if (key === "z") {
    currentOctaveOffset = Math.max(currentOctaveOffset - 12, -36);
    updateOctaveDisplay();
    saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
    return;
  }
  if (key === "x") {
    currentOctaveOffset = Math.min(currentOctaveOffset + 12, 36);
    updateOctaveDisplay();
    saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
    return;
  }

  // Check if the key is mapped and not already playing
  if (KEYBOARD_MAP[key] && !activeKeyboardNotes.has(key)) {
    const midiNote = KEYBOARD_MAP[key] + currentOctaveOffset;
    activeKeyboardNotes.add(key);

    // Only play and handle guess if the note is within the current valid range
    const min = parseInt(document.getElementById("minNote").value);
    const max = parseInt(document.getElementById("maxNote").value);
    if (midiNote >= min && midiNote <= max) {
      playNote(audioContext, player, selectedInstrument, midiNote);
      handleGuess(midiNote);
      updateKeyDisplay(midiNote, true);
    }
  }
}

function handleKeyUp(event) {
  const key = event.key.toLowerCase();

  if (KEYBOARD_MAP[key]) {
    const midiNote = KEYBOARD_MAP[key] + currentOctaveOffset;
    activeKeyboardNotes.delete(key);
    updateKeyDisplay(midiNote, false);
    stopNote(audioContext, midiNote);
  }
}

function updateOctaveDisplay() {
  const baseNote = 60 + currentOctaveOffset; // 60 is middle C (C4)
  const octave = Math.floor((baseNote - 12) / 12);
  document.getElementById("octaveOffset").textContent = `C${octave}`;
}

function populateNoteDropdowns() {
  let minSelect = document.getElementById("minNote");
  let maxSelect = document.getElementById("maxNote");

  minSelect.innerHTML = "";
  maxSelect.innerHTML = "";

  // Add initial A0 to G#1
  for (let note = 9; note < 12; note++) {
    let midiValue = 21 + (note - 9); // Starting from A0 (MIDI 21)
    let option = new Option(`${NOTE_NAMES[note]}0`, midiValue);
    minSelect.add(option.cloneNode(true));
    maxSelect.add(option);
  }

  // Add notes from C1 to C8
  for (let octave = 1; octave <= 8; octave++) {
    for (let i = 0; i < NOTE_NAMES.length; i++) {
      // Stop after C8
      if (octave === 8 && i > 0) break;

      let midiValue = 12 * (octave + 1) + i;
      let option = new Option(`${NOTE_NAMES[i]}${octave}`, midiValue);
      minSelect.add(option.cloneNode(true));
      maxSelect.add(option);
    }
  }
}

function playNoteHandler() {
  // Remove focus from button after click
  document.activeElement.blur();

  if (!audioContext) {
    // If audio isn't initialized yet, queue up a play note after init
    const shouldPlayAfterInit = true;
    initAudio(shouldPlayAfterInit);
    return;
  }

  // If there's no current note, generate a new one
  if (currentNote === null) {
    playRandomNote();
  } else {
    // Replay the current note
    const randomVelocity = document.getElementById("randomVelocity").checked;
    const velocity = randomVelocity
      ? (Math.floor(Math.random() * (127 - 30 + 1)) + 30) / 127
      : 1.0;
    playNote(audioContext, player, selectedInstrument, currentNote, velocity);
  }
}

function playRandomNote() {
  let min = parseInt(document.getElementById("minNote").value);
  let max = parseInt(document.getElementById("maxNote").value);
  let excludeBlack = document.getElementById("excludeBlackKeys").checked;

  if (min >= max) {
    alert("⚠️ Min note must be lower than Max note.");
    return;
  }

  let validNotes = [];
  for (let note = min; note <= max; note++) {
    let noteIndex = note % 12;
    if (excludeBlack && [1, 3, 6, 8, 10].includes(noteIndex)) continue;
    validNotes.push(note);
  }

  if (validNotes.length === 0) {
    alert("⚠️ No valid notes available in this range.");
    return;
  }

  currentNote = validNotes[Math.floor(Math.random() * validNotes.length)];
  hasGuessed = false;
  document.getElementById("feedback").innerHTML = "";
  resetAllKeys();

  console.log(`🎶 Playing MIDI Note: ${currentNote}`);
  const randomVelocity = document.getElementById("randomVelocity").checked;
  const velocity = randomVelocity
    ? (Math.floor(Math.random() * (127 - 30 + 1)) + 30) / 127
    : 1.0;
  playNote(audioContext, player, selectedInstrument, currentNote, velocity);
  saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
}

function handleGuess(guessedNote) {
  if (!currentNote) return;

  let correct = guessedNote === currentNote;
  let feedback = document.getElementById("feedback");
  let guessedKey = document.querySelector(`[data-note="${guessedNote}"]`);

  const getNoteName = (midiNote) => {
    const octave = Math.floor((midiNote - 12) / 12);
    const noteIndex = midiNote % 12;
    return NOTE_NAMES[noteIndex] + octave;
  };

  const guessedNoteName = getNoteName(guessedNote);

  if (correct) {
    feedback.className = "feedback correct";
    feedback.innerHTML = guessedNoteName;
    guessedKey.classList.add("correct");
    if (!hasGuessed) {
      const distance = Math.abs(guessedNote - currentNote);
      totalSemitoneDistance += distance;
      numCorrect++;
    }
    currentNote = null;

    if (document.getElementById("continuousMode").checked) {
      const continuousSpeed = parseFloat(document.getElementById("continuousSpeedSlider").value) * 1000;
      setTimeout(playRandomNote, continuousSpeed);
    }
  } else {
    feedback.className = "feedback wrong";
    feedback.innerHTML = guessedNoteName;
    if (!hasGuessed) {
      const distance = Math.abs(guessedNote - currentNote);
      totalSemitoneDistance += distance;
      numWrong++;
      hasGuessed = true;
    }
    guessedKey.classList.add("wrong");
  }

  updateStats();
  saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
}

function updateStats() {
  const totalGuesses = numCorrect + numWrong;
  document.getElementById("numCorrect").innerText = numCorrect;
  document.getElementById("numWrong").innerText = totalGuesses;
  let accuracy = totalGuesses > 0 ? ((numCorrect / totalGuesses) * 100).toFixed(2) : 0;
  let avgDistance = totalGuesses > 0 ? (totalSemitoneDistance / totalGuesses).toFixed(2) : 0;
  document.getElementById("percentCorrect").innerText = `Accuracy: ${accuracy}%`;
  document.getElementById("avgDistance").innerText = `Average Semitone Distance: ${avgDistance}`;
}

function resetAllKeys() {
  document.querySelectorAll(".key").forEach((key) => {
    key.classList.remove("correct", "wrong", "active");
  });
  let feedback = document.getElementById("feedback");
  feedback.innerHTML = "";
  feedback.className = "feedback";
  activeNotes.clear();
  hasGuessed = false;
}

function resetSettings() {
  // Stop any currently playing notes
  if (audioContext) {
    // Get all active notes and stop them
    Array.from(activeNotes).forEach(note => {
      stopNote(audioContext, note);
    });
  }
  
  // Reset all settings to defaults without reloading
  document.getElementById("minNote").value = "60"; // C4
  document.getElementById("maxNote").value = "72"; // C5
  document.getElementById("excludeBlackKeys").checked = false;
  document.getElementById("continuousMode").checked = false;
  document.getElementById("continuousSpeedSlider").value = "2.0";
  document.getElementById("speedValue").textContent = "2.0s";
  document.getElementById("noteDurationSlider").value = "2.0";
  document.getElementById("durationValue").textContent = "2.0s";
  document.getElementById("randomVelocity").checked = false;
  document.getElementById("instrumentSelect").value = "0";
  
  // Reset stats
  numCorrect = 0;
  numWrong = 0;
  totalSemitoneDistance = 0;
  currentOctaveOffset = 0;
  
  // Update UI
  updateStats();
  updateOctaveDisplay();
  updateKeyboard();
  
  // Save the reset settings
  saveSettings(0, 0, 0, 0);
  
  // Reset current note and feedback
  currentNote = null;
  document.getElementById("feedback").innerHTML = "";
  resetAllKeys();
}

// Add event listeners for settings changes
document
  .getElementById("excludeBlackKeys")
  .addEventListener("change", () => {
    saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
    updateKeyboard();
  });

document
  .getElementById("continuousMode")
  .addEventListener("change", () => {
    saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
  });

document
  .getElementById("randomVelocity")
  .addEventListener("change", () => {
    saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
  });

document
  .getElementById("minNote")
  .addEventListener("change", () => {
    saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
    updateKeyboard();
  });

document
  .getElementById("maxNote")
  .addEventListener("change", () => {
    saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
    updateKeyboard();
  });

// Add event listener for continuous speed slider
document.getElementById('continuousSpeedSlider').addEventListener('input', (e) => {
  document.getElementById('speedValue').textContent = `${e.target.value}s`;
  saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
});

// Add event listener for note duration slider
document.getElementById('noteDurationSlider').addEventListener('input', (e) => {
  document.getElementById('durationValue').textContent = `${e.target.value}s`;
  saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
});

// Modify the instrument change event listener
document.getElementById('instrumentSelect').addEventListener('change', async (e) => {
    const index = parseInt(e.target.value);
    if (window.audioContext && window.player) {
        const newInstrument = await loadInstrument(window.audioContext, window.player, index);
        window.currentInstrument = newInstrument;
        saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
    }
});

// Update the keyboard event listener for play button
document.addEventListener('keydown', (event) => {
  // Check if Enter or Space was pressed and we're not in an input field
  if ((event.key === 'Enter' || event.code === 'Space') && 
      document.activeElement.tagName !== 'INPUT' && 
      document.activeElement.tagName !== 'SELECT') {
    
    // Prevent repeated triggers while holding the key
    if (playKeyPressed) return;
    playKeyPressed = true;
    
    event.preventDefault();  // Prevent page scrolling on spacebar
    
    // Remove focus from any buttons to prevent default browser behavior
    document.activeElement.blur();
    
    playNoteHandler();
  }
});

// Add keyup handler to reset the state
document.addEventListener('keyup', (event) => {
  if (event.key === 'Enter' || event.code === 'Space') {
    playKeyPressed = false;
  }
});

async function initAudio(shouldPlayAfterInit = false) {
  if (audioContext) return; // Already initialized

  document.getElementById("loading").textContent = "Loading instrument...";
  audioContext = new (window.AudioContext || window.webkitAudioContext)();
  window.audioContext = audioContext;
  player = new WebAudioFontPlayer();
  window.player = player;
  player.cancelQueue = true;
  console.log("✅ AudioContext started on interaction");
  
  // Load initial instrument
  const initialInstrumentIndex = parseInt(document.getElementById("instrumentSelect").value);
  selectedInstrument = await loadInstrument(audioContext, player, initialInstrumentIndex);
  window.currentInstrument = selectedInstrument;
  
  // Hide loading message after instrument is loaded
  document.getElementById("loading").style.display = "none";
  
  generateKeyboard(audioContext, player, selectedInstrument, handleGuess);
  setupMIDI();
  
  // If this was triggered by play button, play a note
  if (shouldPlayAfterInit) {
    playRandomNote();
  }
}

function setupMIDI() {
  if (navigator.requestMIDIAccess) {
    navigator
      .requestMIDIAccess({ sysex: false })
      .then(onMIDISuccess, onMIDIFailure);
  } else {
    console.log("WebMIDI is not supported in this browser.");
  }
}

function onMIDISuccess(midiAccess) {
  for (let input of midiAccess.inputs.values()) {
    input.onmidimessage = getMIDIMessage;
  }
}

function onMIDIFailure() {
  console.log("Could not access your MIDI devices.");
}

function getMIDIMessage(message) {
  let command = message.data[0];
  let note = message.data[1];
  let velocity = message.data.length > 2 ? message.data[2] : 0;

  if (command === 144 && velocity > 0) {
    playNote(audioContext, player, selectedInstrument, note, velocity / 127.0);
    handleGuess(note);
    activeNotes.add(note);
    updateKeyDisplay(note, true);
  } else if (command === 128 || (command === 144 && velocity === 0)) {
    stopNote(audioContext, note);
    activeNotes.delete(note);
    updateKeyDisplay(note, false);
  }
}

function updateKeyDisplay(note, isActive) {
  let key = document.querySelector(`[data-note="${note}"]`);
  if (key) {
    if (isActive) {
      key.classList.add("active");
    } else {
      key.classList.remove("active");
    }
  }
}

// Add near the top with other initialization code
function initializePanels() {
    const settingsButton = document.getElementById('settingsButton');
    const settingsPanel = document.getElementById('settingsPanel');
    
    // Create backdrop element
    const backdrop = document.createElement('div');
    backdrop.className = 'popup-backdrop';
    document.body.appendChild(backdrop);
    
    function closeAllPanels() {
        settingsPanel.classList.remove('active');
        backdrop.classList.remove('active');
    }
    
    settingsButton.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = settingsPanel.classList.contains('active');
        closeAllPanels();
        if (!isOpen) {
            settingsPanel.classList.add('active');
            backdrop.classList.add('active');
        }
    });
    
    // Close panels when clicking outside
    backdrop.addEventListener('click', closeAllPanels);
    
    // Close panels on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAllPanels();
    });
}

// Add near the top with other utility functions
function getCurrentInstrumentFromPath() {
  const path = window.location.pathname;
  if (path.includes('/instruments/')) {
    // Extract instrument name from path (e.g. /instruments/piano.html -> piano)
    const instrument = path.split('/').pop().replace('.html', '');
    
    // Find matching instrument index from INSTRUMENTS array
    const instrumentIndex = INSTRUMENTS.findIndex(i => 
      i.name.toLowerCase() === instrument.toLowerCase()
    );
    
    return instrumentIndex >= 0 ? instrumentIndex : null;
  }
  return null;
}

// Modify the initializeApp function
export function initializeApp() {
    // First populate dropdowns with all possible values
    populateNoteDropdowns();
    
    // Then load settings which will set the correct values
    const savedSettings = loadSettings();
    numCorrect = savedSettings.stats.numCorrect;
    numWrong = savedSettings.stats.numWrong;
    totalSemitoneDistance = savedSettings.stats.totalSemitoneDistance;
    currentOctaveOffset = savedSettings.octaveOffset;

    // If no settings exist, set default range to C4-C5
    if (!localStorage.getItem("earTrainingSettings")) {
        document.getElementById("minNote").value = "60"; // C4
        document.getElementById("maxNote").value = "72"; // C5
        // Save these default settings
        saveSettings(numCorrect, numWrong, currentOctaveOffset, totalSemitoneDistance);
    }

    // Check if we're on an instrument-specific page
    const instrumentPageIndex = getCurrentInstrumentFromPath();
    if (instrumentPageIndex !== null) {
        // Set the instrument select but don't disable it
        const instrumentSelect = document.getElementById("instrumentSelect");
        instrumentSelect.value = instrumentPageIndex;
    }

    generateKeyboard(audioContext, player, selectedInstrument, handleGuess);
    updateKeyboard();
    updateOctaveDisplay();
    updateStats();

    // Remove the initial loading message and click listener
    document.getElementById("loading").style.display = "none";
    initializePanels();
}

// Export functions that need to be global
export const globalFunctions = {
  updateKeyboard,
  resetSettings,
  playNoteHandler,
};

// Make functions available on window
Object.assign(window, globalFunctions);

// Add click listener for audio initialization on any interaction
document.addEventListener("click", () => initAudio(), { once: true });
